import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { SelectOption } from './select-options';

/**
 * O Radix não aceita item de valor vazio; o troig usa `''` para "nenhum", "todos" ou
 * "automático". O vazio vira este marcador só dentro do componente — fora dele continua `''`.
 */
const EMPTY = '__vazio__';

interface SelectInputProps<T extends string> {
  value: T | '';
  onValueChange: (value: T | '') => void;
  options: SelectOption<T>[];
  /** Opção que escolhe o vazio de propósito: "Todas as funções", "Sem reserva". */
  emptyLabel?: string;
  /** Texto enquanto nada foi escolhido e o vazio não é uma escolha: "Escolha um modelo". */
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/**
 * Lista de escolha única do sistema — nenhuma tela usa `<select>` nativo (lint). Recebe os
 * atributos que o `FormField` entrega (`{...field}`), então rótulo e erro continuam ligados.
 */
export function SelectInput<T extends string>({
  value,
  onValueChange,
  options,
  emptyLabel,
  placeholder,
  className,
  disabled,
  ...trigger
}: SelectInputProps<T>) {
  const current = value === '' ? (emptyLabel ? EMPTY : '') : value;
  return (
    <Select
      value={current}
      disabled={disabled}
      onValueChange={(next) => onValueChange(next === EMPTY ? '' : (next as T))}
    >
      <SelectTrigger className={cn('w-full', className)} {...trigger}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {emptyLabel && <SelectItem value={EMPTY}>{emptyLabel}</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
